import { useEffect, useState } from "react";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import { getNews, getNewsArticle } from "../../api/news";
import { parseNewsLinks, plainNewsText } from "../../utils/newsLinks";
import styles from "./news.module.css";

export function useNewsResource(params = {}, articleId) {
  const key = JSON.stringify(params);
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null,
  });
  useEffect(() => {
    let active = true;
    setState({ data: null, loading: true, error: null });
    const timer = setTimeout(() => {
      const request =
        articleId === undefined
          ? getNews(JSON.parse(key))
          : getNewsArticle(articleId);
      request
        .then((data) => {
          if (active) setState({ data, loading: false, error: null });
        })
        .catch((error) => {
          if (active) setState({ data: null, loading: false, error });
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [key, articleId, version]);
  return { ...state, retry: () => setVersion((value) => value + 1) };
}

export function Arrow({ diagonal = false }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <path d={diagonal ? "M5 19 19 5M5 5h14v14" : "M4 12h16m-6-6 6 6-6 6"} />
    </svg>
  );
}

export function NewsArtwork() {
  return (
    <div className={styles.artwork} aria-hidden="true">
      <div className={styles.orbit} />
      <div className={styles.orbitTwo} />
      <div className={styles.paperBack} />
      <div className={styles.paper}>
        <span>ЮФУ.ПРО / МЕДИА</span>
        <strong>
          Есть
          <br />
          новости<span>↗</span>
        </strong>
        <div className={styles.paperLines} />
        <span>ЛЮДИ. ИДЕИ. ПЕРЕМЕНЫ.</span>
      </div>
      <div className={styles.spark}>✳</div>
      <span className={styles.dot} />
    </div>
  );
}

export function NewsCover({ article, className = "", eager = false }) {
  const [failedUrl, setFailedUrl] = useState(null);
  if (!article?.cover_url || failedUrl === article.cover_url) return null;
  return (
    <div className={`${styles.cover} ${className}`}>
      <img
        src={article.cover_url}
        alt=""
        loading={eager ? "eager" : "lazy"}
        referrerPolicy="no-referrer"
        onError={() => setFailedUrl(article.cover_url)}
      />
    </div>
  );
}

export function NewsMeta({ article }) {
  return (
    <div className={styles.meta}>
      <span className={styles.category}>{article.category || "События"}</span>
      {article.published_at ? (
        <time dateTime={article.published_at}>
          {new Date(article.published_at).toLocaleDateString("ru-RU", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </time>
      ) : (
        <span>Черновик</span>
      )}
    </div>
  );
}

export function NewsCard({ article, featured = false, compact = false }) {
  return (
    <Link
      to={`/news/${article.id}`}
      data-category={article.category}
      className={`${styles.card} ${featured ? styles.featured : ""} ${compact ? styles.compact : ""}`}
    >
      {!compact && <NewsCover article={article} eager={featured} />}
      <div className={styles.cardContent}>
        <NewsMeta article={article} />
        <h3>{article.title}</h3>
        {!compact && <p>{plainNewsText(article.excerpt)}</p>}
        <span className={styles.readLink}>
          Читать новость <Arrow diagonal />
        </span>
      </div>
    </Link>
  );
}

export function NewsStatus({ resource, empty = false }) {
  if (resource.loading)
    return (
      <div className={styles.loading} role="status">
        Загружаем новости<span>•••</span>
      </div>
    );
  if (resource.error)
    return (
      <div className={styles.empty} role="alert">
        <h2>Не удалось загрузить новости</h2>
        <p>Попробуйте ещё раз — возможно, соединение прервалось.</p>
        <button className={styles.button} onClick={resource.retry}>
          Попробовать снова
        </button>
      </div>
    );
  if (empty)
    return (
      <div className={styles.empty}>
        <span className={styles.emptySymbol} aria-hidden="true">
          ↗
        </span>
        <h2>Здесь скоро появятся новости</h2>
        <p>
          События интенсива, истории команд и важные объявления. Заходите за
          свежими новостями!
        </p>
      </div>
    );
  return null;
}

export function ArticleContent({ article, preview = false }) {
  const Heading = preview ? "h2" : "h1";
  return (
    <article className={styles.article}>
      <header className={styles.articleHeading}>
        <NewsMeta article={article} />
        <Heading>{article.title || "Заголовок новости"}</Heading>
        <p><NewsInlineText text={article.excerpt || "Краткое описание новости появится здесь."} /></p>
      </header>
      <NewsCover article={article} className={styles.articleCover} eager />
      <div className={styles.articleText}>
        {(article.body || "Текст новости появится здесь.")
          .split(/\n\s*\n/)
          .filter(Boolean)
          .map((paragraph, index) => (
            <p key={index}><NewsInlineText text={paragraph} /></p>
          ))}
      </div>
    </article>
  );
}

export function NewsInlineText({ text }) {
  return parseNewsLinks(text).map((part, index) => part.href ? (
    <a
      key={index}
      className={styles.inlineLink}
      href={part.href}
      target="_blank"
      rel="noopener noreferrer"
      title="Откроется в новой вкладке"
    >
      {part.text}
    </a>
  ) : part.text);
}
